import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Maximize2, Film, Music, AtSign, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, mediaUrl } from "@/lib/api";
import { splitAction } from "@/lib/actionWords";

const DANGER = "rounded-sm text-transparent bg-red-500/40 shadow-[0_0_0_1px_rgba(239,68,68,0.8)]";

const RefIcon = ({ r }) => {
  const m = r.mime || "";
  if (m.startsWith("image/")) return <img src={r.preview || mediaUrl(r.url)} alt="" className="w-full h-full object-cover" />;
  if (m.startsWith("video/") && (r.preview || r.url)) return <video src={r.preview || mediaUrl(r.url)} muted preload="metadata" className="w-full h-full object-cover" />;
  return <div className="w-full h-full flex items-center justify-center">{m.startsWith("audio/") ? <Music className="w-4 h-4 text-zinc-400" /> : <Film className="w-4 h-4 text-zinc-400" />}</div>;
};

// After removing attachment #idx (0-based), drop its @image_N tag and shift later tags down (sep "" → @imageN).
export const renumberTags = (text, idx, prefix = "image", sep = "_") => text
  .replace(new RegExp(`@${prefix}_?${idx + 1}\\b ?`, "g"), "")
  .replace(new RegExp(`@${prefix}_?(\\d+)\\b`, "g"), (m, n) => (Number(n) > idx + 1 ? `@${prefix}${sep}${Number(n) - 1}` : m))
  .replace(/ {2,}/g, " ");

export const dropTag = (text, tag) => text.replace(new RegExp(`@${tag}\\b ?`, "g"), "").replace(/ {2,}/g, " ");

// Textarea with a mirrored layer underneath that colours @tags (known tags bright, unknown dim).
const HiTextarea = React.forwardRef(({ value, className, known, danger, ...props }, ref) => {
  const mirror = useRef(null);
  const parts = value.split(/(@[\w.-]+)/g);
  return (
    <div className="relative">
      <div ref={mirror} aria-hidden className={`${className} absolute inset-0 overflow-hidden whitespace-pre-wrap break-words pointer-events-none !text-transparent [scrollbar-gutter:stable]`} data-testid={`${props["data-testid"]}-hl`}>
        {parts.map((p, i) => (p.startsWith("@") && p.length > 1
          ? <mark key={i} className={`rounded-sm text-transparent ${!known || known.has(p.slice(1)) ? (className.includes("bfx") ? "bg-[#41a7f5]/35 shadow-[0_0_0_1px_rgba(65,167,245,0.7)]" : "bg-[#7AF25F]/30 shadow-[0_0_0_1px_rgba(122,242,95,0.7)]") : "bg-amber-400/25 shadow-[0_0_0_1px_rgba(251,191,36,0.6)]"}`}>{p}</mark>
          : danger ? <span key={i}>{splitAction(p).map(([t, hit], j) => (hit ? <mark key={j} className={DANGER} data-action-word="true">{t}</mark> : <span key={j}>{t}</span>))}</span>
          : <span key={i}>{p}</span>))}{"\u200b"}
      </div>
      <textarea ref={ref} value={value} {...props} onScroll={(e) => { if (mirror.current) mirror.current.scrollTop = e.currentTarget.scrollTop; }}
        className={`${className} relative block !bg-transparent [scrollbar-gutter:stable]`} />
    </div>
  );
});

const MentionList = ({ options, hi, pick, testid, tagMode }) => (
  <div className="absolute z-50 left-0 bottom-full mb-2 w-80 max-h-72 overflow-y-auto bg-[#121214] border border-zinc-800 rounded-xl shadow-2xl p-1.5" data-testid={`${testid}-mention-popup`}>
    <div className="px-2 py-1 text-[10px] font-mono uppercase tracking-widest text-zinc-500 flex items-center gap-1"><AtSign className="w-3 h-3" />{tagMode ? "Attached media" : "Recent uploads"}</div>
    {options.length === 0 ? <p className="text-xs text-zinc-500 px-2 py-3">{tagMode ? "Nothing attached yet — attach media first." : "No matching uploads — attach a file first."}</p>
      : options.map((o, i) => {
        const r = tagMode ? o.ref : o;
        return (
          <button key={tagMode ? o.tag : r.id} type="button" onMouseDown={(e) => { e.preventDefault(); pick(o); }} data-testid={`${testid}-mention-${tagMode ? o.tag : r.id}`}
            className={`w-full flex items-center gap-2.5 p-1.5 rounded-lg text-left ${i === hi ? "bg-zinc-800" : "hover:bg-zinc-800/60"}`}>
            <span className="h-9 w-9 rounded-md overflow-hidden border border-zinc-700 bg-black shrink-0"><RefIcon r={r} /></span>
            <span className="min-w-0"><span className="block text-sm text-zinc-200 truncate">{tagMode ? `@${o.tag}` : r.name}</span>
              <span className="block text-[10px] font-mono text-zinc-500 truncate">{tagMode ? r.name : (r.mime || "").split("/")[0]}</span></span>
          </button>
        );
      })}
  </div>
);

// Auto-growing prompt with @-mention picker, fullscreen editor and optional inline improve.
// tags: [{tag, ref}] — when given, @ only offers these attached items and clicking a tag in the text previews it.
export default function PromptBox({ value, onChange, placeholder, references = [], accept = () => true, onMention, onKeyDown,
  testid = "prompt", minRows = 3, maxHeight = 320, className = "", maxChars, improve = false, tags, danger = false, budget }) {
  const lim = budget?.limit || maxChars;
  const used = budget ? budget.used : value.length;
  const ta = useRef(null);
  const fullTa = useRef(null);
  const [mention, setMention] = useState(null); // {start, query, full}
  const [hi, setHi] = useState(0);
  const [full, setFull] = useState(false);
  const [peek, setPeek] = useState(null);
  const [improving, setImproving] = useState(false);
  const [pop, setPop] = useState(0);
  const tagMode = Array.isArray(tags);
  const known = tagMode ? new Set(tags.map((t) => t.tag)) : null;
  const runImprove = async () => {
    if (!value.trim()) { toast.info("Write a prompt first"); return; }
    setImproving(true);
    try {
      const { data } = await api.post("/prompt/improve", { prompt: value, max_chars: budget ? Math.max(200, budget.limit - (budget.used - value.length)) : maxChars || 2000 }, { timeout: 90000 });
      onChange(data.prompt);
      setPop((n) => n + 1);
      toast.success("Prompt improved");
    } catch (e) { toast.error(errMsg(e, "Could not improve the prompt")); } finally { setImproving(false); }
  };

  useLayoutEffect(() => {
    const el = ta.current;
    if (!el) return;
    el.style.height = "auto";
    const lh = parseFloat(getComputedStyle(el).lineHeight) || 22;
    el.style.height = `${Math.min(Math.max(el.scrollHeight, lh * minRows + 4), maxHeight)}px`;
  }, [value, minRows, maxHeight]);

  const q = (mention?.query || "").toLowerCase();
  const options = tagMode ? tags.filter((t) => !q || t.tag.toLowerCase().includes(q))
    : references.filter(accept).filter((r) => !q || (r.name || "").toLowerCase().includes(q));
  useEffect(() => setHi(0), [mention?.query]);

  const detect = (text, caret, isFull) => {
    const m = text.slice(0, caret).match(/(^|\s)@([\w.-]*)$/);
    setMention(m ? { start: caret - m[2].length - 1, query: m[2], full: isFull } : null);
  };
  const peekAt = (el) => {
    if (!tagMode || !el) return;
    const c = el.selectionStart, text = el.value;
    const left = text.slice(0, c).match(/@(\w*)$/), right = text.slice(c).match(/^\w*/);
    const word = left ? left[1] + (right ? right[0] : "") : null;
    setPeek(word ? tags.find((t) => t.tag === word) || null : null);
  };

  useEffect(() => { setPeek(null); }, [value, full]);

  const pick = (o) => {
    const tag = tagMode ? o.tag : onMention?.(o);
    if (!tag || !mention) { setMention(null); return; }
    const el = mention.full ? fullTa.current : ta.current;
    const caret = el?.selectionStart ?? value.length;
    const dup = new RegExp(`@${tag}\\b`).test(value.slice(0, mention.start) + value.slice(caret));
    const insert = dup ? "" : `@${tag} `;
    onChange(`${value.slice(0, mention.start)}${insert}${value.slice(caret)}`);
    setMention(null);
    requestAnimationFrame(() => { const p = mention.start + insert.length; el?.focus(); el?.setSelectionRange(p, p); });
  };

  const keyDown = (e) => {
    if (mention && options.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => (h + 1) % options.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => (h - 1 + options.length) % options.length); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pick(options[hi]); return; }
    }
    if (e.key === "Escape") setMention(null);
    onKeyDown?.(e);
  };
  const handlers = (isFull) => ({
    onChange: (e) => { onChange(e.target.value); detect(e.target.value, e.target.selectionStart, isFull); },
    onKeyDown: keyDown,
    onClick: (e) => peekAt(e.currentTarget),
    onKeyUp: (e) => { if (!e.key.startsWith("Arrow")) return; peekAt(e.currentTarget); },
    onBlur: () => setTimeout(() => setMention(null), 150),
  });

  const Peek = peek && (
    <div className="absolute z-40 bottom-full right-0 mb-1 flex items-center gap-2 rounded-lg border border-zinc-700 bg-[#121214] p-1 pr-2 shadow-xl" data-testid={`${testid}-peek`}>
      <span className="h-12 w-16 rounded overflow-hidden bg-black"><RefIcon r={peek.ref} /></span>
      <span className="text-[11px] font-mono text-zinc-200">@{peek.tag}</span>
      <button type="button" onClick={() => setPeek(null)} className="text-zinc-500 hover:text-white" data-testid={`${testid}-peek-close`}><X className="w-3 h-3" /></button>
    </div>
  );

  return (
    <div className="relative">
      <HiTextarea ref={ta} value={value} rows={minRows} placeholder={placeholder} data-testid={`${testid}-input`} {...handlers(false)} known={known} danger={danger}
        className={`w-full resize-none outline-none leading-relaxed pr-8 ${className}`} />
      {lim ? (
        <div className={`text-right text-[10px] font-mono ${used > lim ? "text-red-400" : used > lim * 0.9 ? "text-amber-400" : "text-zinc-500"}`} data-testid={`${testid}-char-count`}>
          <span data-testid={`${testid}-char-left`}>{(lim - used).toLocaleString()}</span> characters left · {used.toLocaleString()} / {lim.toLocaleString()}{budget?.hidden ? " incl. app instructions" : ""}{used > lim ? " — too long for this model" : ""}
        </div>
      ) : null}
      <button type="button" onClick={() => setFull(true)} title="Open fullscreen editor" data-testid={`${testid}-expand`}
        className="absolute top-1 right-1 h-7 w-7 rounded-md flex items-center justify-center text-zinc-500 hover:text-white hover:bg-zinc-800">
        <Maximize2 className="w-3.5 h-3.5" />
      </button>
      {improve && (
        <button type="button" onClick={runImprove} disabled={improving} title="Improve prompt with Ai Xtudio" data-testid={`${testid}-improve`}
          className="absolute top-9 right-0.5 h-8 w-8 rounded-md flex items-center justify-center hover:bg-zinc-800 disabled:opacity-60">
          {improving ? <Loader2 className="w-4 h-4 animate-spin text-[#7AF25F]" /> : <img key={pop} src="/logo.png" alt="Improve" className={`w-6 h-6 ${pop ? "ax-improve-pop" : ""}`} />}
        </button>
      )}
      {!full && Peek}
      {mention && !mention.full && <MentionList options={options} hi={hi} pick={pick} testid={testid} tagMode={tagMode} />}

      <Dialog open={full} onOpenChange={(o) => { setFull(o); setMention(null); setPeek(null); }}>
        <DialogContent className="bg-[#0D0D0F] border-zinc-800 text-zinc-100 max-w-4xl w-[92vw] h-[80vh] flex flex-col" data-testid={`${testid}-fullscreen`}>
          <DialogHeader><DialogTitle className="font-heading">Edit prompt</DialogTitle></DialogHeader>
          <div className="relative flex-1 flex flex-col min-h-0">
            <HiTextarea ref={fullTa} autoFocus value={value} placeholder={placeholder} data-testid={`${testid}-fullscreen-input`} {...handlers(true)} known={known} danger={danger}
              className="w-full h-[calc(80vh-9rem)] ax-input resize-none text-base leading-relaxed" />
            {full && Peek}
            {mention?.full && <div className="absolute left-3 bottom-3"><div className="relative"><MentionList options={options} hi={hi} pick={pick} testid={`${testid}-fs`} tagMode={tagMode} /></div></div>}
          </div>
          <div className="flex justify-between items-center text-xs text-zinc-500">
            <span data-testid={`${testid}-fullscreen-count`}>{lim ? `${(lim - used).toLocaleString()} characters left of ${lim.toLocaleString()}` : `${value.length} characters`}</span>
            <div className="flex items-center gap-2">
              {improve && <button type="button" onClick={runImprove} disabled={improving} className="ax-chip" data-testid={`${testid}-fullscreen-improve`}>
                {improving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <img src="/logo.png" alt="" className="w-3.5 h-3.5" />}Improve</button>}
              <button onClick={() => setFull(false)} className="ax-generate h-9 px-5" data-testid={`${testid}-fullscreen-done`}>Done</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
